import React, { useCallback, useEffect, useState } from "react";
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
} from "reactflow";
import "reactflow/dist/style.css";
import "../../Css/Styles.css";
import CustomNode from "./CustomNode";
import { useNavigate, useParams } from "react-router-dom";
import apiEndpoints from "../../apiconfig";
import NodeModal from "./nodemodel";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  Tooltip,
  Grid,
  Breadcrumbs,
  Link,
} from "@mui/material";
import { 
  FiPlus, 
  FiSave, 
  FiArrowLeft, 
  FiPlay,
  FiEye,
  FiShare2,
  FiDownload,
  FiSettings,
  FiTrash2
} from "react-icons/fi";
import { HiChevronRight } from "react-icons/hi";

const nodeTypes = {
  custom: CustomNode,
};

const FlowChart = () => {
  const navigate = useNavigate();
  const { flowId } = useParams();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [chartId, setChartId] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [flowName, setFlowName] = useState("Untitled Flow");

  // Delete node function
  const deleteNode = useCallback(
    (nodeId) => {
      if (!nodeId) return;
      
      // Find the node to check if it's a default node
      const nodeToDelete = nodes.find(node => node.id === nodeId);
      if (nodeToDelete?.data?.isDefault) {
        alert("Start node cannot be deleted.");
        return;
      }

      setNodes((prevNodes) => prevNodes.filter((node) => node.id !== nodeId));
      setEdges((prevEdges) =>
        prevEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId)
      );
    },
    [setNodes, setEdges, nodes]
  );

  // Enhanced delete node with confirmation
  const handleDeleteNode = useCallback((nodeId) => {
    const nodeToDelete = nodes.find(node => node.id === nodeId);
    if (nodeToDelete?.data?.isDefault) {
      alert("Start node cannot be deleted.");
      return;
    }

    if (window.confirm("Are you sure you want to delete this node?")) {
      deleteNode(nodeId);
    }
  }, [deleteNode, nodes]);

  // Handle node double click to open modal
  const handleNodeDoubleClick = useCallback(
    (nodeId) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (node) {
        setSelectedNode(node);
        setIsModalOpen(true);
      }
    },
    [nodes]
  );

  // Enhanced node data with callbacks - FIXED VERSION
  const getEnhancedNodeData = useCallback((baseData, nodeId) => {
    return {
      ...baseData,
      onDelete: () => handleDeleteNode(nodeId), // Fixed: pass the actual function with nodeId
      onDoubleClick: () => handleNodeDoubleClick(nodeId), // Fixed: pass the actual function with nodeId
    };
  }, [handleDeleteNode, handleNodeDoubleClick]);

  // Load flow chart from API
  useEffect(() => {
    if (!flowId) return;

    fetch(`${apiEndpoints.flowchart}?flow_id=${flowId}`)
      .then((res) => res.json())
      .then((data) => {
        if (data && data.length > 0) {
          const chart = data[0];
          setChartId(chart.id);

          try {
            let chartData = chart.chart_data;
            if (typeof chartData === "string") {
              chartData = JSON.parse(chartData);
            }

            // Enhance node data with callbacks
            const enhancedNodes = (chartData.nodes || []).map(node => ({
              ...node,
              data: getEnhancedNodeData(node.data, node.id)
            }));

            setNodes(enhancedNodes);
            setEdges(chartData.edges || []);
            
            if (chart.flow_name) {
              setFlowName(chart.flow_name);
            }
          } catch (err) {
            console.error("Error parsing chart_data:", err);
            setNodes([
              {
                id: "1",
                type: "custom",
                position: { x: 250, y: 5 },
                data: getEnhancedNodeData({ 
                  label: "Start Node", 
                  isDefault: true, 
                  flowId, 
                  color: "#25D366" 
                }, "1"),
              },
            ]);
            setEdges([]);
          }
        }
      })
      .catch((err) => console.error("Error loading flowchart:", err));
  }, [flowId, setNodes, setEdges, getEnhancedNodeData]);

  // Add a new node - FIXED VERSION
  const addNode = useCallback(() => {
    const newNodeId = `${Date.now()}`;
    const newNode = {
      id: newNodeId,
      type: "custom",
      position: { x: Math.random() * 400, y: Math.random() * 400 },
      data: getEnhancedNodeData({
        label: `Node ${nodes.length + 1}`,
        isDefault: false,
        flowId,
        color: "#3B82F6",
      }, newNodeId),
    };
    setNodes((prev) => [...prev, newNode]);
  }, [nodes.length, flowId, setNodes, getEnhancedNodeData]);

  // Handle saving node data from modal
  const handleSaveNodeData = (nodeId, formData) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              ...formData,
              label: formData.nodeName || node.data.label,
              // Preserve the callbacks
              onDelete: node.data.onDelete,
              onDoubleClick: node.data.onDoubleClick,
            },
          };
        }
        return node;
      })
    );
  };

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  // Save chart - FIXED to preserve callbacks
  const handleSave = useCallback(() => {
    // Remove callbacks from node data before saving
    const nodesForSave = nodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        onDelete: undefined,
        onDoubleClick: undefined
      }
    }));

    const payload = {
      flow_id: Number(flowId),
      chart_data: { nodes: nodesForSave, edges },
    };

    const method = "POST";
    const url = chartId
      ? `${apiEndpoints.flowchart}?id=${chartId}`
      : apiEndpoints.flowchart;

    fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          if (!chartId) setChartId(data.id);
          alert("Flow chart saved successfully!");
        } else {
          console.error("Save failed:", data);
        }
      })
      .catch((err) => console.error("Error saving flowchart:", err));
  }, [nodes, edges, flowId, chartId]);

  // Delete selected nodes (for keyboard support)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.key === 'Delete' || event.key === 'Backspace') && event.target === document.body) {
        const selectedNodes = nodes.filter(node => node.selected);
        if (selectedNodes.length > 0) {
          event.preventDefault();
          selectedNodes.forEach(node => {
            if (!node.data?.isDefault) {
              handleDeleteNode(node.id);
            }
          });
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [nodes, handleDeleteNode]);

  // Stats for the header
  const stats = {
    nodes: nodes.length,
    edges: edges.length,
    active: nodes.filter(node => node.data?.isActive !== false).length
  };

  return (
    <Box
      sx={{
        bgcolor: "#F8FAFC",
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        fontFamily: "Montserrat, sans-serif",
        p: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {/* Main Container */}
      <Box sx={{ maxWidth: "1400px", margin: "0 auto", width: "100%" }}>
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            mb: 4,
            gap: 3,
          }}
        >
          <Box>
            <Breadcrumbs 
              separator={<HiChevronRight className="text-gray-400" />} 
              sx={{ mb: 2 }}
            >
              <Link
                underline="hover"
                color="inherit"
                onClick={() => navigate("/flow")}
                sx={{ 
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  color: '#64748B'
                }}
              >
                Flow Management
              </Link>
              <Typography 
                sx={{ 
                  fontSize: '0.875rem',
                  color: '#25D366',
                  fontWeight: 600 
                }}
              >
                Flow Builder
              </Typography>
            </Breadcrumbs>
            
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              <Typography 
                variant="h4" 
                fontWeight={700} 
                color="#0F172A"
                sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}
              >
                {flowName}
              </Typography>
              <Chip
                label="Editing"
                size="small"
                sx={{
                  backgroundColor: "#FEF3C7",
                  color: "#92400E",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              />
            </Box>
            <Typography 
              variant="body2" 
              color="#64748B"
              sx={{ maxWidth: "500px" }}
            >
              Design and customize your conversational automation workflow with drag-and-drop nodes
            </Typography>
          </Box>

          {/* Stats Overview */}
          <Box 
            sx={{ 
              display: "flex", 
              gap: 3,
              flexWrap: 'wrap'
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" fontWeight={700} color="#0F172A">
                {stats.nodes}
              </Typography>
              <Typography variant="caption" color="#64748B">
                Nodes
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" fontWeight={700} color="#0F172A">
                {stats.edges}
              </Typography>
              <Typography variant="caption" color="#64748B">
                Connections
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" fontWeight={700} color="#0F172A">
                {stats.active}
              </Typography>
              <Typography variant="caption" color="#64748B">
                Active
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Toolbar Section */}
        <Card
          sx={{
            width: "100%",
            mb: 4,
            borderRadius: "16px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            border: "1px solid #F1F5F9",
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Tooltip title="Add new node to flow">
                    <Button
                      variant="contained"
                      startIcon={<FiPlus />}
                      sx={{
                        bgcolor: "#25D366",
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: "12px",
                        px: 3,
                        "&:hover": { 
                          bgcolor: "#22C35E",
                          transform: "translateY(-1px)",
                          boxShadow: "0 4px 12px rgba(37, 211, 102, 0.3)"
                        },
                        transition: "all 0.2s ease",
                      }}
                      onClick={addNode}
                    >
                      Add Node
                    </Button>
                  </Tooltip>

                  <Tooltip title="Save current flow">
                    <Button
                      variant="outlined"
                      startIcon={<FiSave />}
                      sx={{
                        borderColor: "#E2E8F0",
                        color: "#64748B",
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: "12px",
                        px: 3,
                        "&:hover": { 
                          borderColor: "#25D366",
                          color: "#25D366",
                          bgcolor: "#F0FDF4"
                        },
                        transition: "all 0.2s ease",
                      }}
                      onClick={handleSave}
                    >
                      Save Flow
                    </Button>
                  </Tooltip>

                  <Tooltip title="Delete selected nodes (Del)">
                    <Button
                      variant="outlined"
                      startIcon={<FiTrash2 />}
                      sx={{
                        borderColor: "#FECACA",
                        color: "#DC2626",
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: "12px",
                        px: 3,
                        "&:hover": { 
                          borderColor: "#DC2626",
                          bgcolor: "#FEF2F2"
                        },
                        transition: "all 0.2s ease",
                      }}
                      onClick={() => {
                        const selectedNodes = nodes.filter(node => node.selected);
                        if (selectedNodes.length === 0) {
                          alert("Please select nodes to delete first.");
                          return;
                        }
                        selectedNodes.forEach(node => {
                          if (!node.data?.isDefault) {
                            handleDeleteNode(node.id);
                          }
                        });
                      }}
                    >
                      Delete Selected
                    </Button>
                  </Tooltip>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box 
                  sx={{ 
                    display: "flex", 
                    gap: 2, 
                    flexWrap: "wrap",
                    justifyContent: { xs: "flex-start", md: "flex-end" }
                  }}
                >
                  <Tooltip title="Test flow">
                    <Button
                      variant="outlined"
                      startIcon={<FiPlay />}
                      sx={{
                        borderColor: "#E2E8F0",
                        color: "#64748B",
                        textTransform: "none",
                        borderRadius: "12px",
                        "&:hover": { 
                          borderColor: "#3B82F6",
                          color: "#3B82F6"
                        },
                      }}
                    >
                      Test
                    </Button>
                  </Tooltip>

                  <Tooltip title="Preview flow">
                    <Button
                      variant="outlined"
                      startIcon={<FiEye />}
                      sx={{
                        borderColor: "#E2E8F0",
                        color: "#64748B",
                        textTransform: "none",
                        borderRadius: "12px",
                        "&:hover": { 
                          borderColor: "#8B5CF6",
                          color: "#8B5CF6"
                        },
                      }}
                    >
                      Preview
                    </Button>
                  </Tooltip>

                  <Tooltip title="Back to flows">
                    <Button
                      variant="outlined"
                      startIcon={<FiArrowLeft />}
                      sx={{
                        borderColor: "#E2E8F0",
                        color: "#64748B",
                        textTransform: "none",
                        borderRadius: "12px",
                        "&:hover": { 
                          borderColor: "#EF4444",
                          color: "#EF4444"
                        },
                      }}
                      onClick={() => navigate(-1)}
                    >
                      Back
                    </Button>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Flow Chart Area */}
        <Card
          sx={{
            width: "100%",
            flex: 1,
            borderRadius: "16px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            border: "1px solid #F1F5F9",
            overflow: "hidden",
            height: "70vh",
            minHeight: "600px",
          }}
        >
          <CardContent sx={{ p: 0, height: "100%", position: "relative" }}>
            {/* Canvas Header */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bgcolor: "rgba(255,255,255,0.95)",
                backdropFilter: "blur(8px)",
                borderBottom: "1px solid #F1F5F9",
                zIndex: 10,
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} color="#374151">
                Flow Canvas • Double-click nodes to edit • Click × to delete
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Chip
                  label={`${nodes.length} nodes`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.75rem" }}
                />
                <Chip
                  label={`${edges.length} connections`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.75rem" }}
                />
              </Box>
            </Box>

            {/* React Flow Canvas */}
            <Box sx={{ height: "100%", pt: 7 }}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeDoubleClick={(event, node) => handleNodeDoubleClick(node.id)}
                connectionLineStyle={{ 
                  stroke: "#25D366", 
                  strokeWidth: 2,
                  strokeDasharray: "5,5"
                }}
                connectionLineType="bezier"
                nodeTypes={nodeTypes}
                fitView
                style={{
                  background: "linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)",
                }}
                deleteKeyCode={["Delete", "Backspace"]}
              >
                <Background 
                  variant="dots" 
                  gap={20} 
                  size={1} 
                  color="#E2E8F0"
                />
                <Controls 
                  style={{
                    bottom: 20,
                    right: 20,
                  }}
                />
              </ReactFlow>
            </Box>
          </CardContent>
        </Card>

        {/* Quick Actions Footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="body2" color="#64748B">
            Flow ID: <strong>{flowId}</strong> • Last saved: Just now
          </Typography>
          
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Export flow">
              <Button
                size="small"
                startIcon={<FiDownload />}
                sx={{
                  color: "#64748B",
                  textTransform: "none",
                  fontSize: "0.875rem",
                }}
              >
                Export
              </Button>
            </Tooltip>
            
            <Tooltip title="Share flow">
              <Button
                size="small"
                startIcon={<FiShare2 />}
                sx={{
                  color: "#64748B",
                  textTransform: "none",
                  fontSize: "0.875rem",
                }}
              >
                Share
              </Button>
            </Tooltip>
            
            <Tooltip title="Flow settings">
              <Button
                size="small"
                startIcon={<FiSettings />}
                sx={{
                  color: "#64748B",
                  textTransform: "none",
                  fontSize: "0.875rem",
                }}
              >
                Settings
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Node Modal */}
      <NodeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        node={selectedNode}
        onSave={handleSaveNodeData}
      />
    </Box>
  );
};

export default FlowChart;